import { KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';
import { isEmptyArray, isStringEmpty } from '@common/helpers';
import { PassengerInformationInputModalEditArea } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MListData } from '@common/interfaces';
import {
  BasicReservationContactInformation,
  RepresentativeInformationData,
  RepresentativeInformationEmail,
  RepresentativeInformationParts,
  RepresentativeInformationPhone,
} from '@common/interfaces/mybooking/modals/passenger-information-input/representative-information.interface';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { BehaviorSubject } from 'rxjs';
import { GetOrderResponseDataContactsRepresentativePhonesInner } from 'src/sdk-servicing';
import { GetOrderResponseDataContactsRepresentative } from 'src/sdk-servicing/model/getOrderResponseDataContactsRepresentative';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';

/**
 * 代表者連絡先情報 service
 */
@Injectable()
export class RepresentativeInformationService {
  constructor(
    private readonly _common: CommonLibService,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService
  ) {}

  initialData(): RepresentativeInformationData {
    return {
      basicReservationContactInfo: new BehaviorSubject<BasicReservationContactInformation>(
        {} as BasicReservationContactInformation
      ),
      emails: [],
      phones: [],
      isError: false,
    };
  }

  /**
   * 代表者連絡先情報 初期値の作成
   * @param representative 代表者連絡先情報
   * @returns 代表者連絡先情報 初期値
   */
  createData(representative: GetOrderResponseDataContactsRepresentative): RepresentativeInformationData {
    const isPhoneEditable: boolean = (representative.phones ?? []).length < 2;
    const defaultEmails: RepresentativeInformationEmail[] = [
      {
        email: '',
        emailConfirm: '',
        encryptedAddress: '',
      },
    ];
    const defaultPhones: RepresentativeInformationPhone[] = [
      {
        type: GetOrderResponseDataContactsRepresentativePhonesInner.TypeEnum.M,
        phoneCountry: this._common.isJapaneseOffice() ? 'JP' : '',
        phoneCountryNumber: this._common.isJapaneseOffice() ? '81' : '',
        phoneNumber: '',
        encryptedNumber: '',
      },
    ];

    let emails =
      representative?.emails?.map(
        (email) =>
          ({
            email: email?.address ?? '',
            emailConfirm: email?.address ?? '',
            encryptedAddress: email?.encryptedAddress ?? '',
          } as RepresentativeInformationEmail)
      ) ?? [];

    const phones =
      representative?.phones?.map((phone) => {
        let country = this._s04CommonSvc.masterData.i18nCountries.find(
          (country) => country.international_tel_country_code === phone.countryPhoneExtension
        );

        const country_2letter_code = country?.country_2letter_code ?? '';
        // ※代表者連絡先情報.代表者電話番号の国番号が(＋1)の場合には優先的にアメリカ合衆国/カナダ
        let phoneCountry = country?.international_tel_country_code === '1' ? 'US' : country_2letter_code;

        let phoneCountryNumber = country?.international_tel_country_code ?? '';

        // [2.14] 代表者電話番号国
        // 2.代表者電話番号情報編集可否=true、かつ表示用代表者連絡先情報.代表者電話番号の国番号＝(空欄)、かつユーザ共通情報.操作オフィスコード＝日本の場合、日本の旨
        if (
          isPhoneEditable &&
          isStringEmpty(representative?.phones?.[0]?.countryPhoneExtension) &&
          this._common.isJapaneseOffice()
        ) {
          country = this._s04CommonSvc.masterData.i18nCountries.find(
            (country) => country.country_2letter_code.toLocaleLowerCase() === 'jp'
          );

          phoneCountry = country?.country_2letter_code ?? '';
          phoneCountryNumber = country?.international_tel_country_code ?? '';
        }

        let phoneNumber: string = phone?.number ?? '';
        if (country?.country_2letter_code.toLocaleLowerCase() === 'jp' && phone?.number?.startsWith('0')) {
          phoneNumber = phone?.number?.substring(1);
        }

        let phoneType = phone?.type;
        if (!phoneType || isStringEmpty(phone?.type)) {
          phoneType = GetOrderResponseDataContactsRepresentativePhonesInner.TypeEnum.M;
        }

        return {
          type: phoneType,
          phoneCountry,
          phoneCountryNumber,
          phoneNumber: phoneNumber,
          encryptedNumber: phone?.encryptedNumber ?? '',
        };
      }) ?? [];

    return {
      basicReservationContactInfo: new BehaviorSubject<BasicReservationContactInformation>(
        {} as BasicReservationContactInformation
      ),
      emails: isEmptyArray(emails) ? defaultEmails : emails,
      phones: isEmptyArray(phones) ? defaultPhones : phones,
      isError: false,
    };
  }

  /**
   * 代表者連絡先情報 設定値の作成
   * @param representative 代表者連絡先情報
   * @returns 代表者連絡先情報 設定値
   */
  createParts(representative: GetOrderResponseDataContactsRepresentative): RepresentativeInformationParts {
    const isEditMode: boolean = this._s04CommonSvc.isEditMode(
      PassengerInformationInputModalEditArea.REPRESENTATIVE_CONTACT
    );
    const isRegistered: boolean =
      ((representative?.emails && representative?.emails?.length > 0) ||
        (representative?.phones && representative?.phones?.length > 0)) ??
      false;
    const registrationLabel = this._s04CommonSvc.getRegistrationLabel(isEditMode, isRegistered);
    const registrationClass = this._s04CommonSvc.getRegistrationLabelClass(registrationLabel);

    return {
      isDisplay: false,
      isDisplayBasicReservation: false,
      isDisplayPhone: false,
      isEmailAddressEditable: false,
      isPhoneNumberEditable: false,
      registrationLabel: registrationLabel,
      registrationClass: registrationClass,
    };
  }

  /**
   * 電話種別ボックスラジオ選択肢作成
   * @param Pd010DataList M_LIST_DATE_PD_010取得結果
   * @return 画面表示用電話番号種別リスト
   */
  public createPhoneTypeLabel(Pd010DataList: Array<MListData>): Array<KeyValue<string, string>> {
    // ソート処理,選択肢作成
    return (
      [...Pd010DataList]
        .sort((a: MListData, b: MListData) => a.display_order - b.display_order)
        .map((Pd010) => {
          return {
            key: Pd010.value,
            value: Pd010.display_content,
          };
        }) ?? []
    );
  }

  /**
   * 電話番号種別　APIデータとcacheデータの変換
   * @param code API値
   * @returns キャッシュ値
   */
  public getCachePhoneType(code: string) {
    switch (code) {
      case 'M':
        return 'M1';
      case 'B':
        return 'B1';
      case 'H':
        return 'H1';
      default:
        return code;
    }
  }

  /**
   * 電話番号種別　APIデータとcacheデータの変換
   * @param code キャッシュ値
   * @returns API値
   */
  public getApiPhoneType(code: string) {
    switch (code) {
      case 'M1':
        return 'M';
      case 'B1':
        return 'B';
      case 'H1':
        return 'H';
      default:
        return code;
    }
  }
}
